import React, { type FC } from 'react';

import Box from '../../../Box/Box';
import { BytescaleImage } from '../../../BytescaleImage';
import { Icon } from '../../../Icon';
import { Heading, Text } from '../../../Typography';
import { imageStyles } from './TopMatchingFieldNote.styles';

export type TopMatchingFieldNoteProps = {
  /**
   * Name of the field note author(s)
   */
  author: string;
  /**
   * Date the field note was posted (e.g., "1d ago")
   */
  datePosted: string;
  /**
   * Field note content text
   */
  content?: string;
  /**
   * Array of image URLs to display (can be JSON string or array)
   */
  images: string[] | string;
  /**
   * Title for the top matching field note section
   * @default "Top matching field note"
   */
  title?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Children content (used when rendering from markdown)
   */
  children?: React.ReactNode;
  /**
   * Image width for optimization (2x for retina)
   * @default 752
   */
  width?: number;
  /**
   * Image height for optimization (2x for retina)
   * @default 550
   */
  height?: number;
};

export const TopMatchingFieldNote: FC<TopMatchingFieldNoteProps> = ({
  author,
  datePosted,
  content,
  images,
  title = 'Top matching field note',
  className,
  children,
  width = 752,
  height = 550,
}) => {
  // Parse images if it's a JSON string (from markdown)
  let parsedImages: string[] = [];
  try {
    parsedImages = typeof images === 'string' ? JSON.parse(images) : images;
  } catch (e) {
    console.warn('Failed to parse images in TopMatchingFieldNote:', e);
    parsedImages = [];
  }

  // Use children content if content prop is not provided (for markdown rendering)
  const displayContent = content || children;

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
      <Heading
        size="2xs"
        fontWeight="bold"
        color="var(--text-primary)"
        css={{ marginTop: 'var(--spacing-6)' }}
      >
        {title}
      </Heading>
      <Box
        display="flex"
        flexDirection="column"
        gap="var(--spacing-4)"
        bg="var(--surface-neutral)"
        borderRadius="var(--radius-lg)"
        pt="var(--spacing-2)"
        minWidth="335px"
        className={className}
      >
        <Text size="md" color="var(--text-primary)">
          {displayContent}
        </Text>
        {parsedImages.length > 0 && parsedImages[0] && (
          <BytescaleImage
            imagePath={parsedImages[0]}
            width={width}
            height={height}
            css={imageStyles}
            alt="Field note update"
          />
        )}
        <Box display="flex" alignItems="center" gap="var(--spacing-2)">
          <Icon variant="IconFieldNotesUpdates" size="large" />
          <Text size="xs" color="var(--text-primary)">
            {author} shared an update • {datePosted}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
